import { FC, HTMLAttributes } from 'react'
import styled, { useTheme } from 'styled-components'

import { Info, ResponsiveImage } from '@syconium/little-miss-figgy'

import { ProductCatalogEmbroideryPlacement } from '../../../__generated__/graphql/catalog/graphql'
import { useSiteWideMessaging } from '../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { deriveNameFromEmbroideryPlacement } from '../../../containers/EmbroideryContainer/utils/deriveNameFromEmbroideryPlacement'
import { StyledTooltip } from '../Modal/primitives/shared-form-styles'

import { DetailLine, LearnMoreLink, Visual } from './styles'
import { Template } from './Template'

export interface Props extends HTMLAttributes<HTMLDivElement> {
  backgroundColor: string | undefined
  imgSrc: string | undefined
  logoName: string | undefined
  logoPlacement: ProductCatalogEmbroideryPlacement | string | undefined
  logoColor: string | undefined
  logoNote: string | undefined
  hasLogoSetup: boolean
}

export const DTCLogoSneakPeek: FC<Props> = ({
  backgroundColor,
  imgSrc,
  logoName,
  logoPlacement,
  logoColor,
  logoNote,
  hasLogoSetup,
  ...rest
}) => {
  const logoPlacementString =
    typeof logoPlacement === 'undefined'
      ? undefined
      : typeof logoPlacement === 'string'
      ? logoPlacement
      : deriveNameFromEmbroideryPlacement(logoPlacement)

  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()
  const theme = useTheme()
  const { siteWideLogoDigitizationRequestedTooltipMessage } = useSiteWideMessaging()

  if (!imgSrc) return null
  return (
    <Template.Container className='logo-sneak-peek-container' {...rest}>
      <Template.Header backgroundColor={backgroundColor}>
        <ImageWrapper>
          <ResponsiveImage
            alt={logoName || 'CustomLogo'}
            aspectRatios={{
              sm: null,
              md: null,
            }}
            src={imgSrc}
            loading={'default'}
            widths={{ unit: 'px', sm: 100, md: 100 }}
            transparentBackground={true}
          />
        </ImageWrapper>
      </Template.Header>
      <Template.Body>
        {hasLogoSetup && (
          <LineWrapper>
            <StyledDetailLine $general>
              {fixtures.dtcLogoEmbroideryForm.digitizationRequested}
            </StyledDetailLine>
            <StyledTooltip
              content={
                <>
                  <span>{siteWideLogoDigitizationRequestedTooltipMessage}</span>{' '}
                  <LearnMoreLink
                    target='_blank'
                    rel='noopener'
                    href='https://help.wearfigs.com/hc/en-us/categories/360003960333-Embroidery'
                  >
                    {fixtures.dtcLogoEmbroideryForm.learnMore}.
                  </LearnMoreLink>
                </>
              }
            >
              <button
                type='button'
                aria-label='More information about the logo digitization process'
              >
                <Info height='12px' width='12px' fill={theme.color.fill.strong} />
              </button>
            </StyledTooltip>
          </LineWrapper>
        )}
        <DetailLine $general>
          {fixtures.logoOption.logoLabelLine.replace('{{logoName}}', logoName ?? '')}
        </DetailLine>

        <DetailLine>
          {fixtures.logoOption.logoColorLabel}:{' '}
          {logoColor === 'BLACK_WHITE'
            ? fixtures.dtcLogoEmbroideryForm.logoStrings['LOGO_BLACK_WHITE'].title
            : fixtures.dtcLogoEmbroideryForm.logoStrings['LOGO_COLOR'].title}
        </DetailLine>

        <DetailLine>
          {fixtures.logoOption.logoPlacementLabel}: {logoPlacementString}
        </DetailLine>

        {logoNote && (
          <DetailLine>
            {fixtures.logoOption.logoNoteLabel}: "{logoNote}"
          </DetailLine>
        )}
      </Template.Body>
    </Template.Container>
  )
}

const ImageWrapper = styled(Visual)`
  padding: ${o => o.theme.spacing(0, 3.5)};
`

const StyledDetailLine = styled(DetailLine)`
  text-transform: uppercase;
  font-size: 9px;
  line-height: 12px;
  letter-spacing: 1px;
  margin: ${o => o.theme.spacing(1, 0)};
`

const LineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
`
